import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Button, Divider, FormControl, FormControlLabel, InputLabel, MenuItem,
  Radio, RadioGroup, Select, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { AddOutlined } from '@mui/icons-material';
import ContactSupplierService from '../../../shared/services/contact-supplier/contact-supplier.service';
import { IContactSupplierDetails } from '../../../shared/services/contact-supplier/contact-supplier.model';
import { SupplierPicker } from '../../presentation-components/form-field-components/supplier-picker';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import { UserDetailsContext } from '../../../context/user-details/user-details';
import SurveyAdminService from '../../../shared/services/survey/survey-admin.service';
import ScheduleService from '../../../shared/services/schedule/schedule.service';
import { ICreateSurveyInvitationRequest, SurveyStatus } from '../../../shared/services/survey/survey.model';
import { ISchedule } from '../../../shared/services/schedule/schedule.model';
import { ScheduleDialog } from '../settings/schedule-dialog';

const EMPLOYEE_DEFAULT_TEXT = 'Please complete the employee commuting survey by clicking the button below.';
const SUPPLIER_DEFAULT_TEXT = 'Please complete this short survey by clicking the button below. Your answers help us report our supply chain emissions accurately.';

interface Props {
  open: boolean;
  onClose: () => void;
  successHandler: () => void;
  surveyId: string;
  /** Product-data surveys invite register suppliers only, so responses attribute correctly. */
  supplierOnly?: boolean;
  /** Enables the draft guard: sending to a draft survey offers to activate it first. */
  surveyStatus?: SurveyStatus;
  /** Called when the draft guard activates the survey, so the host can refresh its banner. */
  onActivated?: () => void;
}

export const AddRecipientsDialog = ({ open, onClose, successHandler, surveyId, supplierOnly = false, surveyStatus, onActivated }: Props) => {
  const surveyAdminService = SurveyAdminService.getInstance();
  const scheduleService = new ScheduleService();
  const userNotificationsContext = useContext(UserNotificationsContext);
  const { userDetails } = useContext(UserDetailsContext);
  const isAdmin = userDetails?.roles?.includes('admin') ?? false;

  const contactSupplierService = ContactSupplierService.getInstance();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mode, setMode] = useState<'emails' | 'suppliers'>('emails');
  const [suppliers, setSuppliers] = useState<IContactSupplierDetails[]>([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState<IContactSupplierDetails[]>([]);
  const selectedSupplierIds = useMemo(() => selectedSuppliers.map(supplier => supplier.id), [selectedSuppliers]);
  const [rawInput, setRawInput] = useState('');
  const [customEmailText, setCustomEmailText] = useState(EMPLOYEE_DEFAULT_TEXT);
  const [timing, setTiming] = useState<'now' | 'schedule'>('now');
  const [scheduleId, setScheduleId] = useState('');
  const [schedules, setSchedules] = useState<ISchedule[]>([]);
  const [openCreateSchedule, setOpenCreateSchedule] = useState(false);
  const [draftAction, setDraftAction] = useState<'activate' | 'sendDraft'>('activate');
  // Once the user edits the email body, mode flips stop replacing it.
  const bodyEditedRef = useRef(false);

  const loadSchedules = () =>
    scheduleService.getAll({ perPage: 100, page: 0 })
      .then(r => setSchedules(r.rows))
      .catch(() => setSchedules([]));

  useEffect(() => {
    if (open) {
      const initialMode = supplierOnly ? 'suppliers' : 'emails';
      setMode(initialMode);
      setRawInput('');
      setSelectedSuppliers([]);
      setCustomEmailText(initialMode === 'suppliers' ? SUPPLIER_DEFAULT_TEXT : EMPLOYEE_DEFAULT_TEXT);
      bodyEditedRef.current = false;
      setTiming('now');
      setScheduleId('');
      setDraftAction('activate');
      loadSchedules();
      contactSupplierService.getActiveSuppliersLite()
        .then(setSuppliers)
        .catch(() => setSuppliers([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, supplierOnly]);

  // Keep the default email copy in step with the audience, without clobbering edited text.
  useEffect(() => {
    if (bodyEditedRef.current) return;
    setCustomEmailText(mode === 'suppliers' ? SUPPLIER_DEFAULT_TEXT : EMPLOYEE_DEFAULT_TEXT);
  }, [mode]);

  const parseRecipients = () => {
    return rawInput
      .split('\n')
      .map(line => line.trim())
      .filter(Boolean)
      .map(line => {
        const parts = line.split(',').map(p => p.trim());
        return { email: parts[0], recipientName: parts[1] || undefined };
      })
      .filter(inv => inv.email.includes('@'));
  };

  const parsed = parseRecipients();
  const suppliersWithEmail = selectedSuppliers.filter(s => s.primaryContactEmail && s.primaryContactEmail.includes('@'));
  const recipientCount = mode === 'suppliers' ? suppliersWithEmail.length : parsed.length;

  const isDraft = surveyStatus === 'DRAFT';
  const chosenSchedule = timing === 'schedule' ? scheduleId : '';
  const submitDisabled = recipientCount === 0 || (timing === 'schedule' && !scheduleId);

  const handleSubmit = async () => {
    if (submitDisabled) return;
    setIsSubmitting(true);
    let activated = false;
    try {
      if (isDraft && draftAction === 'activate') {
        await surveyAdminService.updateSurvey(surveyId, { status: 'ACTIVE' });
        activated = true;
        onActivated?.();
      }
      const payload: ICreateSurveyInvitationRequest = {
        recipients: mode === 'suppliers' ? [] : parsed,
        contactSupplierIds: mode === 'suppliers' ? suppliersWithEmail.map(s => s.id) : undefined,
        scheduleId: chosenSchedule || undefined,
        customEmailText,
      };
      await surveyAdminService.createInvitations(surveyId, payload);

      const message = chosenSchedule
        ? `${recipientCount} invitation${recipientCount === 1 ? '' : 's'} added to schedule`
        : `${recipientCount} invitation${recipientCount === 1 ? '' : 's'} sent`;

      userNotificationsContext.openSnackbar({
        open: true,
        message: activated ? `Survey activated. ${message}` : message,
        backgroundColor: 'primary.dark',
      });
      successHandler();
      onClose();
    } catch {
      userNotificationsContext.openSnackbar({
        open: true,
        message: activated
          ? 'Survey activated, but sending failed. Try adding the recipients again.'
          : 'An error occurred, please try again',
        backgroundColor: 'text.primary',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleScheduleCreated = async (newSchedule: ISchedule) => {
    setOpenCreateSchedule(false);
    // Reload the list so the new schedule appears, then auto-select it.
    await loadSchedules();
    setTiming('schedule');
    setScheduleId(newSchedule.id);
  };

  return (
    <>
      <DialogWrapper
        open={open}
        onClose={onClose}
        title='Add Recipients'
        cancelHandler={onClose}
        submitHandler={handleSubmit}
        isSubmitting={isSubmitting}
        disableSaveButton={submitDisabled}
        submitButtonText={isDraft && draftAction === 'activate' ? 'Activate and send' : 'Send'}
        maxWidth='sm'
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          {isDraft && (
            <Alert severity='warning'>
              <Typography variant='body2' sx={{ mb: 1 }}>
                This survey is a draft. Invite links do not work until the survey is activated.
              </Typography>
              <RadioGroup value={draftAction} onChange={e => setDraftAction(e.target.value as 'activate' | 'sendDraft')}>
                <FormControlLabel
                  value='activate'
                  control={<Radio size='small' />}
                  label='Activate the survey and send'
                />
                <FormControlLabel
                  value='sendDraft'
                  control={<Radio size='small' />}
                  label='Send while draft (links start working when the survey is activated)'
                />
              </RadioGroup>
            </Alert>
          )}

          <Typography variant='subtitle2' fontWeight={600}>Who</Typography>

          {!supplierOnly && (
            <ToggleButtonGroup
              size='small'
              exclusive
              value={mode}
              onChange={(_, value) => { if (value) setMode(value) }}
            >
              <ToggleButton value='emails'>Enter emails</ToggleButton>
              <ToggleButton value='suppliers'>From supplier register</ToggleButton>
            </ToggleButtonGroup>
          )}

          {mode === 'emails' && (
            <>
              <Typography variant='body2' color='text.secondary'>
                Enter one recipient per line. Format: <strong>email</strong> or <strong>email, Name</strong>
              </Typography>
              <TextField
                label='Recipients'
                value={rawInput}
                onChange={e => setRawInput(e.target.value)}
                fullWidth
                multiline
                rows={7}
                placeholder={'jane@example.com, Jane Smith\njohn@example.com'}
              />
              {parsed.length > 0 && (
                <Typography variant='caption' color='text.secondary'>
                  {parsed.length} valid recipient{parsed.length === 1 ? '' : 's'} detected
                </Typography>
              )}
            </>
          )}

          {mode === 'suppliers' && (
            <>
              <Typography variant='body2' color='text.secondary'>
                Each selected supplier's primary contact receives the invitation, and their response
                is linked back to the supplier record.
              </Typography>
              <SupplierPicker
                multiple
                suppliers={suppliers}
                value={selectedSupplierIds}
                onChange={(_ids, picked) => setSelectedSuppliers(picked)}
                label='Suppliers'
                placeholder='Select suppliers'
                chipSize='small'
                getOptionDisabled={supplier => !supplier.primaryContactEmail || !supplier.primaryContactEmail.includes('@')}
                renderOptionSecondary={supplier =>
                  supplier.primaryContactEmail && supplier.primaryContactEmail.includes('@')
                    ? supplier.primaryContactEmail
                    : 'No contact email recorded'}
              />
              {selectedSuppliers.length > 0 && (
                <Typography variant='caption' color='text.secondary'>
                  {suppliersWithEmail.length} supplier{suppliersWithEmail.length === 1 ? '' : 's'} will be invited
                </Typography>
              )}
            </>
          )}

          <Divider />

          <Typography variant='subtitle2' fontWeight={600}>Message</Typography>

          <TextField
            label='Email body text'
            value={customEmailText}
            onChange={e => { bodyEditedRef.current = true; setCustomEmailText(e.target.value); }}
            fullWidth
            multiline
            rows={3}
            helperText='Text shown in the email body'
          />

          <Divider />

          <Typography variant='subtitle2' fontWeight={600}>When</Typography>

          <RadioGroup value={timing} onChange={e => setTiming(e.target.value as 'now' | 'schedule')}>
            <FormControlLabel value='now' control={<Radio size='small' />} label='Send now' />
            <FormControlLabel value='schedule' control={<Radio size='small' />} label='Send on a schedule' />
          </RadioGroup>

          {timing === 'schedule' && (
            <>
              <FormControl size='small' fullWidth>
                <InputLabel>Schedule</InputLabel>
                <Select
                  label='Schedule'
                  value={scheduleId}
                  onChange={e => setScheduleId(e.target.value)}
                >
                  {schedules.map(s => {
                    const hasPendingRun = s.active && !!s.nextRunAt;
                    return (
                      <MenuItem key={s.id} value={s.id} disabled={!hasPendingRun}>
                        {s.name}
                        {hasPendingRun ? (
                          <Typography variant='caption' color='text.secondary' sx={{ ml: 1 }}>
                            (next: {new Date(s.nextRunAt!).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })})
                          </Typography>
                        ) : (
                          <Typography variant='caption' color='text.disabled' sx={{ ml: 1 }}>
                            No pending runs
                          </Typography>
                        )}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              {isAdmin && (
                <Button
                  size='small'
                  variant='text'
                  startIcon={<AddOutlined />}
                  onClick={() => setOpenCreateSchedule(true)}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Create new schedule
                </Button>
              )}

              <Typography variant='caption' color='text.secondary'>
                Invitations will be queued and sent when this schedule fires.
              </Typography>
            </>
          )}
        </Stack>
      </DialogWrapper>

      <ScheduleDialog
        open={openCreateSchedule}
        onClose={() => setOpenCreateSchedule(false)}
        successHandler={() => {}}
        defaultActions={[{ actionType: 'send_survey_invitations', surveyId }]}
        onCreated={handleScheduleCreated}
      />
    </>
  );
};
